import axios from 'axios';
import store, { persistor } from '../store';
import { logoutUserSuccess } from '../slices/auth/login/reducer';
import { api } from '../config';
import { userManager } from '../config/oidcConfig';
import { clearLocalStorageKeepDevice } from './deviceId';

// REACT_APP_IDLE_TIMEOUT_MINUTES hanya untuk testing; produksi tidak di-set (default 15).
// Nilai ini HARUS sama dengan BE appsettings Session:IdleTimeoutMinutes.
export const IDLE_TIMEOUT_MS =
  (Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 15) * 60 * 1000;
export const IDLE_WARNING_MS = 60 * 1000; // modal muncul 60 dtk sebelum logout
export const HEARTBEAT_INTERVAL_MS = 30 * 1000;

const LAST_ACTIVITY_KEY = 'hydro:lastActivity';
export const SESSION_ENDED_KEY = 'hydro:sessionEnded';
// Sinyal login sukses ke tab lain. Tanpa ini tab yang masih di /login (state logged-out di memori)
// bisa menimpa persist:root bersama saat state-nya berubah (mis. banner error auto-dismiss).
export const SESSION_STARTED_KEY = 'hydro:sessionStarted';
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

// Kode dari BE (SessionEndReasons, BE #406). UNAUTHENTICATED sengaja tidak masuk:
// request tanpa token bukan berarti sesi berakhir.
export const SESSION_END_CODES = [
  'IDLE_TIMEOUT',
  'SIGNED_IN_ELSEWHERE',
  'SESSION_REPLACED',
  'SESSION_EXPIRED',
  'REVOKED_ALL',
  'TOKEN_REFRESHED',
  'USER_LOGOUT',
  'SESSION_LOGGED_OUT',
  'SESSION_INVALID',
];

let lastWrite = 0;
export const markActivity = (force = false) => {
  const now = Date.now();
  if (!force && now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    // storage penuh/diblok: idle tetap dijaga BE
  }
};

export const getLastActivity = () => {
  const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  return Number.isFinite(v) && v > 0 ? v : null;
};

// Ambil kode sesi dari error axios (sebelum interceptor mengubahnya jadi string) atau dari Response fetch + body.
export const getSessionEndCode = (status, body) => {
  if (status !== 401) return null;
  const code = body?.data?.code;
  return SESSION_END_CODES.includes(code) ? code : null;
};

// Dipanggil thunk login setelah dispatch sukses: tulis state login ke storage dulu, baru beri tahu tab lain.
export const announceSessionStarted = async () => {
  await persistor.flush();
  localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
};

let ending = false;
export const forceLogout = async (
  code,
  { notifyServer = false, broadcast = true } = {}
) => {
  if (ending) return; // banyak request 401 bersamaan → cukup sekali
  ending = true;

  const token = store.getState()?.Login?.user?.token;
  if (notifyServer && token) {
    try {
      await Promise.race([
        axios.post(`${api.API_URL}/account/logout`, null, {
          params: { reason: code },
          headers: { Authorization: `Bearer ${token}` },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 3000)
        ),
      ]);
    } catch {
      // best-effort: BE mungkin sudah memutus sesi (401) — abaikan
    }
  }

  // Lokal saja: TIDAK memanggil signoutRedirect() ke IdP IDAMAN.
  // broadcast=false = tab penerima sinyal: localStorage (bersama) sudah/sedang diurus tab asal —
  // jangan dihapus di sini supaya tidak mengganggu signoutRedirect() Logout.js di tab asal.
  try {
    store.dispatch(logoutUserSuccess());
    await persistor.purge();
    if (broadcast) await userManager.removeUser();
  } catch {
    // lanjut bersihkan storage apa pun yang terjadi
  }
  sessionStorage.clear();
  if (broadcast) {
    clearLocalStorageKeepDevice();
    // Ditulis setelah clear() → memicu event 'storage' di tab lain.
    localStorage.setItem(
      SESSION_ENDED_KEY,
      JSON.stringify({ code, at: Date.now() })
    );
  }
  window.location.href =
    code === 'USER_LOGOUT' ? '/login' : `/login?reason=${code}`;
};
